import { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, Button as MuiButton } from '@material-ui/core';
import FusePageCarded from '@fuse/core/FusePageCarded/FusePageCarded';
import { BsGearFill } from 'react-icons/bs';
import { FcCalendar } from 'react-icons/fc';
import { AiOutlineDisconnect } from 'react-icons/ai';
import { PiGearSixDuotone, PiFactoryDuotone, PiChartLineUpDuotone, PiClockCounterClockwiseDuotone, PiCheckCircleDuotone, PiPlayCircleDuotone } from 'react-icons/pi';
import { format, isValid } from 'date-fns';

import PipelineConnectorOverlay from 'app/admin/optimizationPipelineV2/component/pipeline/components/PipelineConnectorOverlay';
import DateTimeSelector from 'app/admin/optimizationPipelineV2/component/pipeline/components/DateTimeSelector';

import UnitNode from 'app/admin/optimizationPipelineV2/component/pipeline/nodes/UnitNode';
import DataValidationNode from 'app/admin/optimizationPipelineV2/component/pipeline/nodes/DataValidationNode';
import ModelValidationNode from 'app/admin/optimizationPipelineV2/component/pipeline/nodes/ModelValidationNode';
import UnitOptimizerNode from 'app/admin/optimizationPipelineV2/component/pipeline/nodes/UnitOptimizerNode';
import EDTDynamicCheckNode from 'app/admin/optimizationPipelineV2/component/pipeline/nodes/EDTDynamicCheckNode';
import ApproveNode from 'app/admin/optimizationPipelineV2/component/pipeline/nodes/ApproveNode';

import OptimizationPipelineSidebar from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebar';
import UnitMeasurementSidebar, { UnitMeasurementSidebarHeaderActions } from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/UnitMeasurementSidebar';
import UnitOptimizerSidebar, { UnitOptimizerSidebarHeaderActions } from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/UnitOptimizerSidebar';
import ModelValidationSidebar, { ModelValidationSidebarHeaderActions } from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/ModelValidationSidebar';

import UnitHistoryPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/UnitHistoryPopup';
import DataValidationHistoryPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/DataValidationHistoryPopup';
import ModelValidationHistoryPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/ModelValidationHistoryPopup';
import ModelValidationDetailsChartPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/ModelValidationDetailsChartPopup';
import ApproveSettingsPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/ApproveSettingsPopup';
import DynamicValidationSettingsPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/DynamicValidationSettingsPopup';
import DynamicValidationPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/DynamicValidationPopup';
import UnitOptimizerHistoryPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/UnitOptimizerHistoryPopup';
import UnitOptimizerDetailsChartPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/UnitOptimizerDetailsChartPopup';
import DynamicValidationHistoryPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/DynamicValidationHistoryPopup';
import DynamicValidationDetailsPopup from 'app/admin/optimizationPipelineV2/component/pipeline/popups/DynamicValidationDetailsPopup';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSummaryResolution } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';
import PageHeaderBackButton from 'modules/ui/component/PageHeaderBackButton';

const MOBILE_LAYOUT_MAX_WIDTH = 720;
const COMPACT_LAYOUT_MAX_WIDTH = 1180;

const NODE_REGISTRY = {
    unit: UnitNode,
    dataValidation: DataValidationNode,
    modelValidation: ModelValidationNode,
    unitOptimizer: UnitOptimizerNode,
    edt: EDTDynamicCheckNode,
    approve: ApproveNode
};

const SIDEBAR_COMPONENT_REGISTRY = {
    UNIT_MEASUREMENT: {
        title: 'Unit Measurement',
        icon: PiFactoryDuotone,
        Component: UnitMeasurementSidebar,
        HeaderActions: UnitMeasurementSidebarHeaderActions
    },
    UNIT_OPTIMIZER: {
        title: 'Unit Optimizer',
        icon: PiGearSixDuotone,
        Component: UnitOptimizerSidebar,
        HeaderActions: UnitOptimizerSidebarHeaderActions
    },
    MODEL_VALIDATION: {
        title: 'Model Validation',
        icon: PiChartLineUpDuotone,
        Component: ModelValidationSidebar,
        HeaderActions: ModelValidationSidebarHeaderActions
    }
};

const POPUP_COMPONENT_REGISTRY = {
    UNIT_HISTORY: {
        title: 'Unit History',
        icon: PiClockCounterClockwiseDuotone,
        Component: UnitHistoryPopup
    },
    DATA_VALIDATION_HISTORY: {
        title: 'Data Validation History',
        icon: PiClockCounterClockwiseDuotone,
        Component: DataValidationHistoryPopup
    },
    MODEL_VALIDATION_HISTORY: {
        title: 'Model Validation History',
        icon: PiClockCounterClockwiseDuotone,
        Component: ModelValidationHistoryPopup
    },
    MODEL_VALIDATION_DETAILS_CHART: {
        title: 'Model Validation Details',
        icon: PiChartLineUpDuotone,
        Component: ModelValidationDetailsChartPopup
    },
    APPROVE_SETTINGS: {
        title: 'Approve Settings',
        icon: PiCheckCircleDuotone,
        Component: ApproveSettingsPopup
    },
    DYNAMIC_VALIDATION_SETTINGS: {
        title: 'Dynamic Validation Settings',
        icon: PiPlayCircleDuotone,
        Component: DynamicValidationSettingsPopup
    },
    DYNAMIC_VALIDATION: {
        title: 'Dynamic Validation',
        icon: PiPlayCircleDuotone,
        Component: DynamicValidationPopup
    },
    UNIT_OPTIMIZER_HISTORY: {
        title: 'Unit Optimizer History',
        icon: PiClockCounterClockwiseDuotone,
        Component: UnitOptimizerHistoryPopup
    },
    UNIT_OPTIMIZER_DETAILS_CHART: {
        title: 'Unit Optimizer Details',
        icon: PiGearSixDuotone,
        Component: UnitOptimizerDetailsChartPopup
    },
    DYNAMIC_VALIDATION_HISTORY: {
        title: 'EDT Dynamic Check History',
        icon: PiClockCounterClockwiseDuotone,
        Component: DynamicValidationHistoryPopup
    },
    DYNAMIC_VALIDATION_DETAILS: {
        title: 'EDT Dynamic Check Details',
        icon: PiPlayCircleDuotone,
        Component: DynamicValidationDetailsPopup
    }
};

const LAYOUT_CONFIG = {
    desktop: {
        grid: { rows: 2, cols: 4 },
        maxWidth: '1440px',
        nodes: {
            core: [
                { id: 'n1', key: 'unit', row: 1, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n2', key: 'dataValidation', row: 1, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n3', key: 'modelValidation', row: 1, col: 3, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n5', key: 'unitOptimizer', row: 1, col: 4, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n7', key: 'approve', row: 2, col: 2, w: 1, h: 1, heightMode: 'auto' }
            ],
            withEdt: [
                { id: 'n1', key: 'unit', row: 1, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n2', key: 'dataValidation', row: 1, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n3', key: 'modelValidation', row: 1, col: 3, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n5', key: 'unitOptimizer', row: 1, col: 4, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n6', key: 'edt', row: 2, col: 3, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n7', key: 'approve', row: 2, col: 2, w: 1, h: 1, heightMode: 'auto' }
            ]
        },
        connections: {
            core: [
                { from: 'n1', to: 'n2', fromSide: 'right', toSide: 'left' },
                { from: 'n2', to: 'n3', fromSide: 'right', toSide: 'left' },
                { from: 'n3', to: 'n5', fromSide: 'right', toSide: 'left' },
                { from: 'n5', to: 'n7', fromSide: 'bottom', toSide: 'right', route: 'vfirst' },
                { from: 'n7', to: 'n1', fromSide: 'left', toSide: 'bottom', route: 'hfirst' }
            ],
            withEdt: [
                { from: 'n1', to: 'n2', fromSide: 'right', toSide: 'left' },
                { from: 'n2', to: 'n3', fromSide: 'right', toSide: 'left' },
                { from: 'n3', to: 'n5', fromSide: 'right', toSide: 'left' },
                { from: 'n5', to: 'n6', fromSide: 'bottom', toSide: 'right', route: 'vfirst' },
                { from: 'n6', to: 'n7', fromSide: 'left', toSide: 'right', route: 'hfirst' },
                { from: 'n7', to: 'n1', fromSide: 'left', toSide: 'bottom', route: 'hfirst' }
            ]
        }
    },
    compact: {
        grid: { rows: 3, cols: 2 },
        maxWidth: '980px',
        nodes: {
            core: [
                { id: 'n1', key: 'unit', row: 1, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n2', key: 'dataValidation', row: 1, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n3', key: 'modelValidation', row: 2, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n5', key: 'unitOptimizer', row: 3, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n7', key: 'approve', row: 2, col: 1, w: 1, h: 1, heightMode: 'auto' }
            ],
            withEdt: [
                { id: 'n1', key: 'unit', row: 1, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n2', key: 'dataValidation', row: 1, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n3', key: 'modelValidation', row: 2, col: 2, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n7', key: 'approve', row: 2, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n6', key: 'edt', row: 3, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n5', key: 'unitOptimizer', row: 3, col: 2, w: 1, h: 1, heightMode: 'auto' }
            ]
        },
        connections: {
            core: [
                { from: 'n1', to: 'n2', fromSide: 'right', toSide: 'left' },
                { from: 'n2', to: 'n3', fromSide: 'bottom', toSide: 'top' },
                { from: 'n3', to: 'n5', fromSide: 'bottom', toSide: 'top' },
                { from: 'n5', to: 'n7', fromSide: 'left', toSide: 'bottom', route: 'hfirst' },
                { from: 'n7', to: 'n1', fromSide: 'top', toSide: 'bottom' }
            ],
            withEdt: [
                { from: 'n1', to: 'n2', fromSide: 'right', toSide: 'left' },
                { from: 'n2', to: 'n3', fromSide: 'bottom', toSide: 'top' },
                { from: 'n3', to: 'n5', fromSide: 'bottom', toSide: 'top' },
                { from: 'n5', to: 'n6', fromSide: 'left', toSide: 'right', route: 'hfirst' },
                { from: 'n6', to: 'n7', fromSide: 'top', toSide: 'bottom' },
                { from: 'n7', to: 'n1', fromSide: 'top', toSide: 'bottom' }
            ]
        }
    },
    mobile: {
        grid: { rows: 6, cols: 1 },
        maxWidth: '640px',
        nodes: {
            core: [
                { id: 'n1', key: 'unit', row: 1, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n2', key: 'dataValidation', row: 2, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n3', key: 'modelValidation', row: 3, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n5', key: 'unitOptimizer', row: 4, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n7', key: 'approve', row: 5, col: 1, w: 1, h: 1, heightMode: 'auto' }
            ],
            withEdt: [
                { id: 'n1', key: 'unit', row: 1, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n2', key: 'dataValidation', row: 2, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n3', key: 'modelValidation', row: 3, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n5', key: 'unitOptimizer', row: 4, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n6', key: 'edt', row: 5, col: 1, w: 1, h: 1, heightMode: 'auto' },
                { id: 'n7', key: 'approve', row: 6, col: 1, w: 1, h: 1, heightMode: 'auto' }
            ]
        },
        connections: { core: [], withEdt: [] }
    }
};

function getLayoutMode(containerWidth) {
    if (containerWidth > 0 && containerWidth <= MOBILE_LAYOUT_MAX_WIDTH) return 'mobile';
    if (containerWidth > 0 && containerWidth <= COMPACT_LAYOUT_MAX_WIDTH) return 'compact';

    return 'desktop';
}

function getPipelineLayoutConfig(layoutMode, hasEdtDynamicCheck, noPiWriter) {
    const layoutConfig = LAYOUT_CONFIG[layoutMode] || LAYOUT_CONFIG.desktop;
    const nodes = hasEdtDynamicCheck ? layoutConfig.nodes.withEdt : layoutConfig.nodes.core;
    const connections = hasEdtDynamicCheck ? layoutConfig.connections.withEdt : layoutConfig.connections.core;

    const normalizedNodes = nodes.map((node) => {
        if (node.key === 'approve' && noPiWriter) return { ...node, renderAsIcon: true };

        return node;
    });

    const approveNode = normalizedNodes.find((node) => node.key === 'approve');
    const approveNodeId = approveNode?.id;

    const normalizedConnections = connections
        .filter((connection) => {
            const fromExists = normalizedNodes.some((node) => node.id === connection.from);
            const toExists = normalizedNodes.some((node) => node.id === connection.to);

            return fromExists && toExists;
        })
        .map((connection) => {
            if (!noPiWriter) return connection;
            if (approveNodeId && (connection.from === approveNodeId || connection.to === approveNodeId)) return { ...connection, dashed: true };

            return connection;
        });

    return {
        grid: layoutConfig.grid,
        maxWidth: layoutConfig.maxWidth,
        nodes: normalizedNodes,
        connections: normalizedConnections
    };
}

function formatCurrentViewMessage(ts, summaryDurationUnit) {
    if (!ts) return null;

    const date = ts instanceof Date ? ts : new Date(ts);
    if (!isValid(date)) return null;

    const resolution = getSummaryResolution(summaryDurationUnit);

    return 'You are currently viewing data for ' + format(date, resolution === 'day' ? 'yyyy.MM.dd.' : 'yyyy.MM.dd. HH:mm');
}

function ApproveIcon(props) {
    return (
        <div className='w-full h-full min-h-[120px] flex items-center justify-center'>
            <div ref={props.anchorRef} className='w-56 sm:w-64 md:w-72 h-16 flex items-center justify-center'>
                <AiOutlineDisconnect className='text-40 sm:text-44 text-grey-300 drop-shadow-md' />
            </div>
        </div>
    );
}

export default function OptimizationPipeline() {
    const { id, optimizationPipelineData, ts, setTs, sidebar, closeSidebar, popup, timestamps } = useOptimizationPipeline();
    const { t } = useTranslation(['optimizationPipelineV2']);

    const history = useHistory();

    const containerRef = useRef(null);
    const wrapperRef = useRef(null);
    const nodeRefs = useRef({});
    const anchorRefs = useRef({});

    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        let frameId = null;

        function updateWidth() {
            const nextWidth = container.getBoundingClientRect().width || 0;

            setContainerWidth((prevWidth) => {
                if (Math.abs(prevWidth - nextWidth) < 1) return prevWidth;

                return nextWidth;
            });
        }

        function scheduleUpdate() {
            if (frameId) cancelAnimationFrame(frameId);

            frameId = requestAnimationFrame(() => {
                updateWidth();
                frameId = null;
            });
        }

        scheduleUpdate();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', scheduleUpdate);

            return () => {
                window.removeEventListener('resize', scheduleUpdate);

                if (frameId) cancelAnimationFrame(frameId);
            };
        }

        const observer = new ResizeObserver(() => { scheduleUpdate(); });
        observer.observe(container);

        return () => {
            observer.disconnect();

            if (frameId) cancelAnimationFrame(frameId);
        };
    }, []);

    const hasEdtDynamicCheck = !!optimizationPipelineData?.edtDynamicCheck;
    const noPiWriter = !optimizationPipelineData?.piWriter;
    const layoutMode = getLayoutMode(containerWidth);
    const currentViewMessage = formatCurrentViewMessage(ts, optimizationPipelineData?.summaryDurationUnit);
    const layoutConfig = getPipelineLayoutConfig(layoutMode, hasEdtDynamicCheck, noPiWriter);
    const hasNoTimestampData = Array.isArray(timestamps) && timestamps.length === 0;

    const nodeById = {};
    layoutConfig.nodes.forEach((node) => { nodeById[node.id] = node; });

    const sidebarDefinition = sidebar?.type ? SIDEBAR_COMPONENT_REGISTRY[sidebar.type] : null;
    const sidebarTitle = sidebar?.title || sidebarDefinition?.title || '';
    const sidebarIcon = sidebar?.icon || sidebarDefinition?.icon || null;
    const SidebarContent = sidebarDefinition?.Component || null;
    const SidebarHeaderActions = sidebarDefinition?.HeaderActions || null;

    const popupDefinition = popup?.type ? POPUP_COMPONENT_REGISTRY[popup.type] : null;
    const PopupContent = popupDefinition?.Component || null;

    const isMobile = layoutMode === 'mobile';
    const isCompact = layoutMode === 'compact';
    const contentGap = '3.2rem';

    function handleTsChange(newDate) {
        setTs(newDate);
    }

    return (
        <div className='relative w-full h-full min-h-0'>

            <FusePageCarded
                innerScroll
                classes={{ content: 'flex flex-col min-h-0 overflow-hidden', contentCard: 'flex flex-col min-h-0 overflow-hidden', header: 'min-h-72 h-72 sm:h-136 sm:min-h-136' }}
                header={
                    <div className='self-center flex items-center w-full max-w-full h-96 sm:h-72'>

                        <div className='w-full flex flex-col sm:flex-row gap-16 items-center justify-between'>

                            <div className='w-full flex flex-col gap-2'>

                                <PageHeaderBackButton to='/optimization-pipeline-v2' />

                                <Typography className='hidden sm:flex text-16 md:text-24 mx-12 font-semibold'>
                                    {optimizationPipelineData?.name || 'Optimization Pipeline'}
                                </Typography>

                                {currentViewMessage && (
                                    <div className='hidden sm:flex items-center gap-8 mx-12 text-12 text-gray-500 font-medium'>
                                        <FcCalendar className='shrink-0' />
                                        <span>{currentViewMessage}</span>
                                    </div>
                                )}

                            </div>

                            <div className='w-full sm:w-auto flex flex-col sm:flex-row items-center gap-16'>

                                <div className='w-full sm:w-auto flex items-center gap-4'>
                                    <MuiButton className='flex-1 sm:flex-none whitespace-nowrap mx-4' variant='contained' color='secondary' onClick={() => { history.push('/optimization-pipeline-v2/edit/' + id); }} startIcon={<BsGearFill className='w-16 h-16 hidden sm:flex' />}>Settings</MuiButton>
                                </div>

                                <DateTimeSelector value={ts} onChange={handleTsChange} />

                            </div>

                        </div>

                    </div>
                }
                content={
                    hasNoTimestampData ? (
                        <div className='w-full h-full min-h-[320px] flex items-center justify-center p-32 xl:p-72'>
                            <div className='w-full flex flex-col items-center justify-center text-center px-24'>
                                <FcCalendar className='text-64 mb-16' />
                                <Typography className='text-20 font-semibold text-gray-700'>
                                    {t('noTimestampDataTitle')}
                                </Typography>
                                <Typography className='text-13 text-gray-500 mt-6 max-w-320'>
                                    {t('noTimestampDataDescription')}
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        <div ref={containerRef} className={'w-full min-h-full ' + (isMobile ? 'px-32' : 'px-32 xl:px-72')}>
                            <div className='w-full min-h-full flex flex-col items-center'>
                                <div style={{ flex: '1 0 ' + contentGap, minHeight: contentGap }} />

                                <div
                                    ref={wrapperRef}
                                    className={'w-full grid relative transition-all duration-300 ease-in-out transform filter ' + (isMobile ? 'gap-28' : 'gap-76')}
                                    style={{ maxWidth: isMobile ? '640px' : (isCompact ? '980px' : layoutConfig.maxWidth), gridTemplateColumns: 'repeat(' + layoutConfig.grid.cols + ', minmax(0, 1fr))', gridTemplateRows: 'repeat(' + layoutConfig.grid.rows + ', auto)' }}
                                >

                                    {!isMobile && (
                                        <PipelineConnectorOverlay
                                            wrapperRef={wrapperRef}
                                            nodeRefs={nodeRefs}
                                            anchorRefs={anchorRefs}
                                            nodes={layoutConfig.nodes}
                                            nodeById={nodeById}
                                            connections={layoutConfig.connections}
                                        />
                                    )}

                                    {layoutConfig.nodes.map((node) => {
                                        const Comp = NODE_REGISTRY[node.key];

                                        if (!Comp && !node.renderAsIcon) return null;

                                        const cellStyle = {
                                            gridColumn: node.col + ' / span ' + node.w,
                                            gridRow: node.row + ' / span ' + node.h,
                                            alignSelf: 'stretch'
                                        };

                                        return (
                                            <div
                                                key={node.id}
                                                className='flex items-center justify-center'
                                                style={cellStyle}
                                                ref={(el) => {
                                                    if (el) nodeRefs.current[node.id] = el;
                                                    else delete nodeRefs.current[node.id];
                                                }}
                                                data-node-id={node.id}
                                            >
                                                {node.renderAsIcon ? (
                                                    <ApproveIcon
                                                        anchorRef={(el) => {
                                                            if (el) anchorRefs.current[node.id] = el;
                                                            else delete anchorRefs.current[node.id];
                                                        }}
                                                    />
                                                ) : (
                                                    <Comp
                                                        fit={(node.heightMode ?? 'auto') === 'fit'}
                                                        anchorRef={(el) => {
                                                            if (el) anchorRefs.current[node.id] = el;
                                                            else delete anchorRefs.current[node.id];
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}

                                </div>

                                <div style={{ flex: '1 1 0', minHeight: 0 }} />
                            </div>

                            <div style={{ height: contentGap }} />
                        </div>
                    )
                }
            />

            <OptimizationPipelineSidebar
                open={!!sidebar}
                title={sidebarTitle}
                icon={sidebarIcon}
                onClose={closeSidebar}
                headerRight={SidebarHeaderActions ? <SidebarHeaderActions /> : null}
                width={sidebar?.width}
                minWidth={sidebar?.minWidth}
                maxWidth={sidebar?.maxWidth}
                className={sidebar?.className}
            >
                {SidebarContent ? <SidebarContent /> : null}
            </OptimizationPipelineSidebar>

            {PopupContent ? <PopupContent {...popup} /> : null}

        </div>
    );
}
